import React, { useEffect, useState, Component } from 'react';
import { X, ExternalLink, Loader2, Bug, BookOpen, Zap, Layers, Clock, User, Tag, Link as LinkIcon, MessageSquare, AlertCircle, ArrowUpRight, GitBranch, ChevronRight, Paperclip, FileText, Image, FileCode, File } from 'lucide-react';
import { runTool } from '../../services/toolRunner';
interface IssueDetailPanelProps {
  issueKey: string | null;
  onClose: () => void;
  jiraBaseUrl: string;
}
function getTypeIcon(type: string, size = 14) {
  const t = type.toLowerCase();
  if (t.includes('bug')) return <Bug size={size} className="text-red-400" data-id="element-5061" />;
  if (t.includes('story') || t.includes('historia')) return <BookOpen size={size} className="text-emerald-400" data-id="element-5062" />;
  if (t.includes('epic') || t.includes('épica')) return <Layers size={size} className="text-violet-400" data-id="element-5063" />;
  if (t.includes('sub')) return <GitBranch size={size} className="text-cyan-400" data-id="element-5064" />;
  return <Zap size={size} className="text-blue-400" data-id="element-5065" />;
}
function getPriorityColor(priority: string) {
  const p = priority.toLowerCase();
  if (p.includes('high') || p.includes('highest') || p.includes('blocker') || p.includes('critical')) return 'text-red-400 bg-red-950/50 border-red-900/40';
  if (p.includes('low') || p.includes('lowest') || p.includes('trivial')) return 'text-blue-400 bg-blue-950/50 border-blue-900/40';
  return 'text-amber-400 bg-amber-950/50 border-amber-900/40';
}
function getStatusColor(statusName: string) {
  const s = statusName.toLowerCase();
  if (s.includes('done') || s.includes('closed') || s.includes('resolved')) return 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30';
  if (s.includes('progress') || s.includes('desarrollo') || s.includes('implementa')) return 'bg-blue-500/15 text-blue-400 border-blue-500/30';
  if (s.includes('review') || s.includes('testing') || s.includes('qa')) return 'bg-amber-500/15 text-amber-400 border-amber-500/30';
  if (s.includes('selected') || s.includes('pendiente')) return 'bg-cyan-500/15 text-cyan-400 border-cyan-500/30';
  return 'bg-slate-700/50 text-slate-300 border-slate-600/50';
}
/** Extract plain text from Atlassian Document Format (ADF) or return string as-is */
function extractDescription(desc: any): string {
  if (!desc) return '';
  if (typeof desc === 'string') return desc;
  if (desc.type === 'doc' && Array.isArray(desc.content)) {
    return desc.content.map((block: any) => {
      if (block.type === 'paragraph' && Array.isArray(block.content)) {
        return block.content.map((inline: any) => {
          if (inline.type === 'text') return inline.text || '';
          if (inline.type === 'hardBreak') return '\n';
          if (inline.type === 'mention') return `@${inline.attrs?.text || ''}`;
          if (inline.type === 'inlineCard') return inline.attrs?.url || '[link]';
          return '';
        }).join('');
      }
      if (block.type === 'heading' && Array.isArray(block.content)) {
        return block.content.map((i: any) => i.text || '').join('');
      }
      if (block.type === 'bulletList' && Array.isArray(block.content)) {
        return block.content.map((item: any) => {
          const text = item.content?.flatMap((p: any) => p.content?.map((i: any) => i.text || '') || []).join('');
          return `• ${text}`;
        }).join('\n');
      }
      if (block.type === 'orderedList' && Array.isArray(block.content)) {
        return block.content.map((item: any, idx: number) => {
          const text = item.content?.flatMap((p: any) => p.content?.map((i: any) => i.text || '') || []).join('');
          return `${idx + 1}. ${text}`;
        }).join('\n');
      }
      if (block.type === 'codeBlock') {
        const text = block.content?.map((i: any) => i.text || '').join('') || '';
        return `\`\`\`\n${text}\n\`\`\``;
      }
      if (block.type === 'blockquote' && Array.isArray(block.content)) {
        return block.content.flatMap((p: any) => p.content?.map((i: any) => `> ${i.text || ''}`) || []).join('\n');
      }
      if (block.type === 'rule') return '───';
      return '';
    }).filter(Boolean).join('\n\n');
  }
  try {
    return JSON.stringify(desc, null, 2);
  } catch {
    return String(desc);
  }
}
function formatTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  if (diffMins < 1) return 'ahora';
  if (diffMins < 60) return `hace ${diffMins}m`;
  if (diffHours < 24) return `hace ${diffHours}h`;
  if (diffDays < 7) return `hace ${diffDays}d`;
  if (diffDays < 30) return `hace ${Math.floor(diffDays / 7)} sem`;
  return date.toLocaleDateString();
}
function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-AR', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
}
export function IssueDetailPanel({
  issueKey,
  onClose,
  jiraBaseUrl
}: IssueDetailPanelProps) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  // Store the real Jira key resolved from the API (handles numeric ID → real key)
  const [resolvedKey, setResolvedKey] = useState<string | null>(null);
  useEffect(() => {
    if (!issueKey) {
      setData(null);
      setError(null);
      setResolvedKey(null);
      return;
    }
    let cancelled = false;
    const fetchIssue = async () => {
      setLoading(true);
      setError(null);
      try {
        const id = issueKey.startsWith('#') ? issueKey.replace('#', '') : issueKey;
        const result = await runTool('get-issue', {
          issue_key: id
        });
        if (cancelled) return;
        if (result.success && result.data) {
          // Preserve the real key from top-level response (e.g. "SRTA-117")
          // even when the panel was opened with a numeric ID like "166973"
          const realKey = result.data.key || null;
          setResolvedKey(realKey);
          setData(result.data.fields || result.data);
        } else {
          setError(result.error || 'No se pudo cargar el issue');
        }
      } catch (err: any) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchIssue();
    return () => {
      cancelled = true;
    };
  }, [issueKey]);
  if (!issueKey) return null;
  // Use the resolved key from API when available, fallback to prop
  const displayKey = resolvedKey || issueKey;
  // Helper: detect if a value looks like a Jira key (PROJ-123) vs a numeric ID
  const isJiraKey = (val: any): boolean => {
    if (!val || typeof val !== 'string') return false;
    return /^[A-Z][A-Z0-9]+-\d+$/.test(val);
  };
  // Extract epic key — prefer structured data, fallback to customfield only if it's a real key
  const rawEpicKey = data?.epic?.key || data?.customfield_10014;
  const epicKey = isJiraKey(rawEpicKey) ? rawEpicKey : undefined;
  const epicName = data?.epic?.name || data?.customfield_10011 || undefined;
  // Extract parent — Jira next-gen uses parent.key, classic uses parent.id
  const rawParentKey = data?.parent?.key;
  const parentKey = isJiraKey(rawParentKey) ? rawParentKey : undefined;
  const parentSummary = data?.parent?.fields?.summary;
  const parentType = data?.parent?.fields?.issuetype?.name;
  // If we have a numeric-only epic/parent ID, show it as secondary info but not as a link
  const numericId = !epicKey && rawEpicKey && /^\d+$/.test(String(rawEpicKey)) ? `#${rawEpicKey}` : !parentKey && rawParentKey && /^\d+$/.test(String(rawParentKey)) ? `#${rawParentKey}` : undefined;
  const description = extractDescription(data?.description);
  const comments = data?.comment?.comments || [];
  return <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-slate-950/60 backdrop-blur-sm z-40 animate-in fade-in duration-200" onClick={onClose} data-id="element-5066" />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-full max-w-[680px] bg-[#1a1d23] border-l border-slate-800/80 shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300" data-id="element-5067">
        {/* ── Header ── */}
        <div className="px-6 py-4 border-b border-slate-800/80 bg-[#1a1d23] shrink-0" data-id="element-5068">
          {/* Breadcrumb: Parent/Epic > Current */}
          <div className="flex items-center gap-1.5 mb-2 text-xs" data-id="element-5069">
            {(parentKey || epicKey) && <>
                <a href={`${jiraBaseUrl}/${parentKey || epicKey}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-5070">
                  {getTypeIcon(parentType || 'Epic', 12)}
                  <span className="font-mono" data-id="element-5071">{parentKey || epicKey}</span>
                </a>
                <ChevronRight size={12} className="text-slate-600" data-id="element-5072" />
              </>}
            <a href={`${jiraBaseUrl}/${displayKey}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-5073">
              {data?.issuetype && getTypeIcon(data.issuetype.name || '', 12)}
              <span className="font-mono" data-id="element-5074">{displayKey}</span>
              <ExternalLink size={10} className="opacity-50" data-id="element-5075" />
            </a>
          </div>

          {/* Title + Status + Close */}
          <div className="flex items-start justify-between gap-3" data-id="element-5076">
            <div className="flex-1 min-w-0" data-id="element-5077">
              {loading ? <div className="h-7 bg-slate-800 rounded w-3/4 animate-pulse" data-id="element-5078" /> : <h2 className="text-lg font-bold text-slate-50 leading-snug" data-id="element-5079">
                  {data?.summary || 'Cargando...'}
                </h2>}
            </div>
            <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-slate-200 hover:bg-slate-800 rounded-lg transition-colors shrink-0" data-id="element-5080">
              <X size={18} data-id="element-5081" />
            </button>
          </div>

          {/* Status badge below title */}
          {data?.status && <div className="mt-2" data-id="element-5082">
              <span className={`inline-block px-3 py-1 rounded-md text-xs font-semibold border ${getStatusColor(data.status.name || '')}`} data-id="element-5083">
                {data.status.name}
              </span>
            </div>}
        </div>

        {/* ── Body ── */}
        <div className="flex-1 overflow-y-auto scroll-smooth scrollbar-thin scrollbar-thumb-slate-700" data-id="element-5084">
          {loading ? <div className="flex flex-col items-center justify-center h-48 text-slate-500 gap-3" data-id="element-5085">
              <Loader2 size={22} className="animate-spin text-indigo-500" data-id="element-5086" />
              <p className="text-sm" data-id="element-5087">Obteniendo detalles de Jira...</p>
            </div> : error ? <div className="m-6 flex flex-col items-center justify-center h-40 text-red-400 gap-3 bg-red-950/20 rounded-xl border border-red-900/50" data-id="element-5088">
              <AlertCircle size={22} data-id="element-5089" />
              <p className="text-sm" data-id="element-5090">{error}</p>
            </div> : data ? <div className="flex" data-id="element-5091">
              {/* ── Main Content ── */}
              <div className="flex-1 min-w-0 border-r border-slate-800/60" data-id="element-5092">
                {/* Description */}
                <div className="p-6 border-b border-slate-800/40" data-id="element-5093">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3" data-id="element-5094">
                    Descripción
                  </h3>
                  <div className="text-[13px] text-slate-300 leading-relaxed whitespace-pre-wrap" data-id="element-5095">
                    {description || <span className="text-slate-600 italic" data-id="element-5096">
                        Sin descripción
                      </span>}
                  </div>
                </div>

                {/* Linked Issues */}
                {data.issuelinks && data.issuelinks.length > 0 && <div className="p-6 border-b border-slate-800/40" data-id="element-5097">
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-5098">
                      <LinkIcon size={12} data-id="element-5099" />
                      Issues Vinculados
                    </h3>
                    <div className="space-y-1" data-id="element-5100">
                      {data.issuelinks.map((link: any, idx: number) => {
                  const li = link.outwardIssue || link.inwardIssue;
                  if (!li) return null;
                  const dir = link.outwardIssue ? 'outward' : 'inward';
                  const rel = link.type?.[dir] || link.type?.name || 'Relates';
                  const st = li.fields?.status?.name || '';
                  return <a key={idx} href={`${jiraBaseUrl}/${li.key}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-800/50 transition-colors group/li" data-id="element-5101">
                            <span className="text-[10px] text-slate-500 w-[80px] shrink-0 truncate" data-id="element-5102">
                              {rel}
                            </span>
                            {getTypeIcon(li.fields?.issuetype?.name || '', 13)}
                            <span className="text-xs font-mono text-indigo-400 group-hover/li:text-indigo-300 shrink-0" data-id="element-5103">
                              {li.key}
                            </span>
                            <span className="text-[13px] text-slate-200 flex-1 min-w-0 truncate" data-id="element-5104">
                              {li.fields?.summary}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${getStatusColor(st)}`} data-id="element-5105">
                              {st}
                            </span>
                          </a>;
                })}
                    </div>
                  </div>}

                {/* Subtasks */}
                {data.subtasks && data.subtasks.length > 0 && <div className="p-6 border-b border-slate-800/40" data-id="element-5106">
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-5107">
                      <GitBranch size={12} data-id="element-5108" />
                      Subtareas · {data.subtasks.length}
                    </h3>
                    <div className="space-y-1" data-id="element-5109">
                      {data.subtasks.map((sub: any) => {
                  const st = sub.fields?.status?.name || '';
                  return <a key={sub.key} href={`${jiraBaseUrl}/${sub.key}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-slate-800/50 transition-colors group/sub" data-id="element-5110">
                            {getTypeIcon(sub.fields?.issuetype?.name || 'Sub-task', 13)}
                            <span className="text-xs font-mono text-indigo-400 group-hover/sub:text-indigo-300 shrink-0" data-id="element-5111">
                              {sub.key}
                            </span>
                            <span className="text-[13px] text-slate-200 flex-1 min-w-0 truncate" data-id="element-5112">
                              {sub.fields?.summary}
                            </span>
                            <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${getStatusColor(st)}`} data-id="element-5113">
                              {st}
                            </span>
                          </a>;
                })}
                    </div>
                  </div>}

                {/* Attachments */}
                {data.attachment && data.attachment.length > 0 && <div className="p-6 border-b border-slate-800/40" data-id="element-5114">
                    <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5" data-id="element-5115">
                      <Paperclip size={12} data-id="element-5116" />
                      Adjuntos · {data.attachment.length}
                    </h3>
                    <div className="grid grid-cols-2 gap-2" data-id="element-5117">
                      {data.attachment.map((att: any) => {
                  const isImage = att.mimeType?.startsWith('image/');
                  const isPdf = att.mimeType === 'application/pdf';
                  const isCode = att.mimeType?.includes('text/') || att.filename?.match(/\.(json|xml|csv|yml|yaml|ts|tsx|js|jsx|py|java|sql)$/i);
                  const sizeKb = att.size ? Math.round(att.size / 1024) : null;
                  const sizeLabel = sizeKb ? sizeKb > 1024 ? `${(sizeKb / 1024).toFixed(1)} MB` : `${sizeKb} KB` : '';
                  const FileIcon = isImage ? Image : isPdf ? FileText : isCode ? FileCode : File;
                  const iconColor = isImage ? 'text-emerald-400' : isPdf ? 'text-red-400' : isCode ? 'text-cyan-400' : 'text-slate-400';
                  return <a key={att.id || att.filename} href={`${jiraBaseUrl.replace('/browse', '')}/secure/attachment/${att.id}/${encodeURIComponent(att.filename)}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2.5 px-3 py-2.5 rounded-md bg-slate-800/30 border border-slate-800/50 hover:bg-slate-800/60 hover:border-slate-700 transition-colors group/att" data-id="element-5118">
                            <FileIcon size={16} className={iconColor} data-id="element-5119" />
                            <div className="flex-1 min-w-0" data-id="element-5120">
                              <p className="text-xs text-slate-200 truncate group-hover/att:text-slate-100" data-id="element-5121">
                                {att.filename}
                              </p>
                              <p className="text-[10px] text-slate-500" data-id="element-5122">
                                {sizeLabel}
                                {att.author?.displayName && ` · ${att.author.displayName}`}
                              </p>
                            </div>
                            <ExternalLink size={10} className="text-slate-600 group-hover/att:text-slate-400 shrink-0" data-id="element-5123" />
                          </a>;
                })}
                    </div>
                  </div>}

                {/* ── Activity / Comments ── */}
                <div className="p-6" data-id="element-5124">
                  <h3 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-4 flex items-center gap-1.5" data-id="element-5125">
                    <MessageSquare size={12} data-id="element-5126" />
                    Actividad · {comments.length} comentarios
                  </h3>

                  {comments.length === 0 ? <p className="text-sm text-slate-600 italic" data-id="element-5127">
                      Sin comentarios
                    </p> : <div className="space-y-0" data-id="element-5128">
                      {comments.slice().reverse().slice(0, 15).map((c: any, idx: number) => {
                  const author = c.author?.displayName || 'Usuario';
                  const avatar = c.author?.avatarUrls?.['24x24'];
                  const created = c.created ? new Date(c.created) : null;
                  const body = extractDescription(c.body);
                  return <div key={c.id || idx} className="flex gap-3 py-4 border-b border-slate-800/30 last:border-0" data-id="element-5129">
                              {/* Avatar */}
                              {avatar ? <img src={avatar} alt="" className="w-8 h-8 rounded-full shrink-0 mt-0.5" data-id="element-5130" /> : <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center shrink-0 mt-0.5" data-id="element-5131">
                                  <User size={14} className="text-slate-500" data-id="element-5132" />
                                </div>}
                              {/* Content */}
                              <div className="flex-1 min-w-0" data-id="element-5133">
                                <div className="flex items-baseline gap-2 mb-1" data-id="element-5134">
                                  <span className="text-sm font-semibold text-slate-200" data-id="element-5135">
                                    {author}
                                  </span>
                                  {created && <span className="text-[11px] text-slate-500" data-id="element-5136">
                                      {formatDate(c.created)} ·{' '}
                                      {formatTimeAgo(created)}
                                    </span>}
                                </div>
                                <div className="text-[13px] text-slate-300 leading-relaxed whitespace-pre-wrap" data-id="element-5137">
                                  {body || <span className="text-slate-600 italic" data-id="element-5138">
                                      Sin contenido
                                    </span>}
                                </div>
                              </div>
                            </div>;
                })}
                      {comments.length > 15 && <p className="text-center text-[11px] text-slate-500 pt-3" data-id="element-5139">
                          Mostrando últimos 15 de {comments.length} comentarios
                        </p>}
                    </div>}
                </div>
              </div>

              {/* ── Sidebar (Jira-style) ── */}
              <div className="w-52 shrink-0 p-4 space-y-5" data-id="element-5140">
                {/* Assignee */}
                <div data-id="element-5141">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5" data-id="element-5142">
                    Assignee
                  </span>
                  <div className="flex items-center gap-2" data-id="element-5143">
                    {data.assignee?.avatarUrls?.['24x24'] ? <img src={data.assignee.avatarUrls['24x24']} alt="" className="w-6 h-6 rounded-full" data-id="element-5144" /> : <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center" data-id="element-5145">
                        <User size={12} className="text-slate-500" data-id="element-5146" />
                      </div>}
                    <span className="text-xs text-slate-200 truncate" data-id="element-5147">
                      {data.assignee?.displayName || 'Sin asignar'}
                    </span>
                  </div>
                </div>

                {/* Reporter */}
                <div data-id="element-5148">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5" data-id="element-5149">
                    Reporter
                  </span>
                  <div className="flex items-center gap-2" data-id="element-5150">
                    {data.reporter?.avatarUrls?.['24x24'] ? <img src={data.reporter.avatarUrls['24x24']} alt="" className="w-6 h-6 rounded-full" data-id="element-5151" /> : <div className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center" data-id="element-5152">
                        <User size={12} className="text-slate-500" data-id="element-5153" />
                      </div>}
                    <span className="text-xs text-slate-200 truncate" data-id="element-5154">
                      {data.reporter?.displayName || 'Desconocido'}
                    </span>
                  </div>
                </div>

                {/* Priority */}
                <div data-id="element-5155">
                  <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5" data-id="element-5156">
                    Prioridad
                  </span>
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(data.priority?.name || '')}`} data-id="element-5157">
                    {data.priority?.name || 'Medium'}
                  </span>
                </div>

                {/* Parent */}
                {(parentKey || epicKey) && <div data-id="element-5158">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5" data-id="element-5159">
                      Parent
                    </span>
                    <a href={`${jiraBaseUrl}/${parentKey || epicKey}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300 transition-colors" data-id="element-5160">
                      {getTypeIcon(parentType || 'Epic', 12)}
                      <span className="font-mono" data-id="element-5161">{parentKey || epicKey}</span>
                      <ArrowUpRight size={10} className="opacity-50" data-id="element-5162" />
                    </a>
                    {(parentSummary || epicName) && <p className="text-[11px] text-slate-400 mt-0.5 truncate" data-id="element-5163">
                        {parentSummary || epicName}
                      </p>}
                  </div>}

                {/* Story Points */}
                {(data.customfield_10016 || data.story_points || data.story_point_estimate) && <div data-id="element-5164">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5" data-id="element-5165">
                      Story Points
                    </span>
                    <span className="text-sm font-mono font-bold text-slate-200" data-id="element-5166">
                      {data.customfield_10016 || data.story_points || data.story_point_estimate}
                    </span>
                  </div>}

                {/* Labels */}
                {data.labels && data.labels.length > 0 && <div data-id="element-5167">
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider font-semibold block mb-1.5" data-id="element-5168">
                      Labels
                    </span>
                    <div className="flex flex-wrap gap-1" data-id="element-5169">
                      {data.labels.map((l: string) => <span key={l} className="px-1.5 py-0.5 bg-slate-800 border border-slate-700 text-slate-300 rounded text-[10px]" data-id="element-5170">
                          {l}
                        </span>)}
                    </div>
                  </div>}

                {/* Dates */}
                <div className="pt-3 border-t border-slate-800/60 space-y-2" data-id="element-5171">
                  {data.created && <div className="flex items-center justify-between text-[11px]" data-id="element-5172">
                      <span className="text-slate-500" data-id="element-5173">Creado</span>
                      <span className="text-slate-300" data-id="element-5174">
                        {formatDate(data.created)}
                      </span>
                    </div>}
                  {data.updated && <div className="flex items-center justify-between text-[11px]" data-id="element-5175">
                      <span className="text-slate-500" data-id="element-5176">Actualizado</span>
                      <span className="text-slate-300" data-id="element-5177">
                        {formatDate(data.updated)}
                      </span>
                    </div>}
                  {data.duedate && <div className="flex items-center justify-between text-[11px]" data-id="element-5178">
                      <span className="text-slate-500" data-id="element-5179">Vencimiento</span>
                      <span className="text-red-400 font-medium" data-id="element-5180">
                        {formatDate(data.duedate)}
                      </span>
                    </div>}
                  {(data.customfield_10015 || data.startdate) && <div className="flex items-center justify-between text-[11px]" data-id="element-5181">
                      <span className="text-slate-500" data-id="element-5182">Inicio</span>
                      <span className="text-slate-300" data-id="element-5183">
                        {formatDate(data.customfield_10015 || data.startdate)}
                      </span>
                    </div>}
                </div>

                {/* Open in Jira */}
                <a href={`${jiraBaseUrl}/${displayKey}`} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-1.5 w-full px-3 py-2 bg-indigo-600/20 border border-indigo-500/30 rounded-lg text-xs font-medium text-indigo-300 hover:bg-indigo-600/30 transition-colors" data-id="element-5184">
                  <ExternalLink size={12} data-id="element-5185" />
                  Abrir en Jira
                </a>
              </div>
            </div> : null}
        </div>
      </div>
    </>;
}